import React, { useEffect, useState } from "react";

import FuzzySearch from "fuzzy-search";

import { type ColumnConfig } from "@features/sales-v3/hooks/use-table-state";
import {
  type MetricCategorizer,
  MetricCategory,
  type SalesPageMetricWithChildren,
} from "@features/sales-v3/utils/metric-categorizer";
import { TextInput } from "@shared/text-input";

import { MetricSelectionGroup } from "./metric-selection-group";

type Props = {
  selectedCategory: MetricCategory;
  metricCategories: MetricCategorizer;
  selectedColumnConfigs: ColumnConfig[];
  selectManyMetrics: (metrics: SalesPageMetricWithChildren[]) => void;
  selectMetric: (metric: SalesPageMetricWithChildren) => void;
  clearAllByCategory: (category: MetricCategory) => void;
  deselectMetric: (metric: SalesPageMetricWithChildren) => void;
};

export const AllMetricsPanel = ({
  selectedCategory,
  metricCategories,
  selectedColumnConfigs,
  selectManyMetrics,
  selectMetric,
  clearAllByCategory,
  deselectMetric,
}: Props) => {
  const [search, setSearch] = useState("");
  const clearSearch = () => setSearch("");
  const metricCategoryNames = Object.values(MetricCategory).filter(
    (category) => category !== MetricCategory.ALL,
  );

  useEffect(() => {
    clearSearch();
  }, [selectedCategory]);

  return (
    <div className="px-4 overflow-y-hidden h-[calc(100%-24px)]">
      <TextInput
        className="mt-1"
        placeholder="Search..."
        leftIcon={<i className="fa-regular fa-sharp fa-search" />}
        value={search}
        onChange={({ target }) => setSearch(target.value)}
      />
      <div className="overflow-y-auto h-[calc(100%-40px)]">
        {metricCategoryNames.map((category) => {
          const metricsByCategory =
            metricCategories.getMetricsByCategory(category);
          const searcher = new FuzzySearch(metricsByCategory, ["name"]);
          const filteredMetrics =
            search === "" ? metricsByCategory : searcher.search(search);

          if (filteredMetrics.length === 0) {
            return null;
          }

          return (
            <MetricSelectionGroup
              key={category}
              listClassName="mb-6"
              selectedCategory={category}
              metrics={filteredMetrics}
              selectedColumnConfigs={selectedColumnConfigs}
              selectManyMetrics={selectManyMetrics}
              selectMetric={selectMetric}
              clearAllByCategory={clearAllByCategory}
              deselectMetric={deselectMetric}
            />
          );
        })}
      </div>
    </div>
  );
};
